/**
 * VH-83: Review/edit an AI-generated or photo-parsed recipe draft before save.
 *
 * The draft is plumbed straight into CaptureManualForm; we stack a header on
 * top with confidence flags and a "discard" action. The category needs to be
 * mapped from suggested_category_key → category_id (lookup in categories).
 */

import { useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { ExclamationTriangleIcon, ArrowUturnLeftIcon } from '@heroicons/react/24/outline'
import CaptureManualForm from './CaptureManualForm'

export default function RecipeDraftReview({
  draft,
  categories = [],
  onSubmit,
  onDiscard,
  submitting,
}) {
  const { t } = useTranslation()

  const initialValue = useMemo(() => {
    if (!draft) return null
    let categoryId = ''
    if (draft.suggested_category_key) {
      const match = categories.find((c) => c.key === draft.suggested_category_key)
      if (match) categoryId = match.id
    }
    return {
      title: draft.title || '',
      description: draft.description || '',
      // VH-106: carry AI-extracted tips into the editable draft form.
      tips: draft.tips || '',
      photo_url: draft.photo_url || '',
      servings: draft.servings || 1,
      prep_minutes: draft.prep_minutes ?? null,
      cook_minutes: draft.cook_minutes ?? null,
      ingredients: (draft.ingredients || []).map((i, idx) => ({
        ...i,
        display_order: idx,
        amount: i.amount ?? '',
      })),
      steps: (draft.steps || []).map((s, idx) => ({
        ...s,
        step_order: s.step_order ?? idx + 1,
      })),
      equipment_keys: draft.equipment_keys || [],
      category_id: categoryId,
    }
  }, [draft, categories])

  // Re-mount the inner form when the draft changes so its useMemo defaults reset.
  useEffect(() => {}, [draft])

  if (!draft) return null

  const flags = draft.confidence_flags || []

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-start gap-4">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">
            {t('recipes.draft.reviewTitle')}
          </h3>
          <p className="text-sm text-gray-600">
            {t('recipes.draft.reviewSubtitle')}
          </p>
        </div>
        <button
          type="button"
          onClick={onDiscard}
          className="btn-secondary text-sm flex items-center gap-1"
        >
          <ArrowUturnLeftIcon className="h-4 w-4" />
          {t('recipes.draft.discard')}
        </button>
      </div>

      {flags.length > 0 && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 p-3">
          <div className="flex items-start gap-2">
            <ExclamationTriangleIcon className="h-5 w-5 flex-shrink-0 text-amber-500" />
            <div>
              <p className="text-sm font-semibold text-amber-800">
                {t('recipes.draft.lowConfidence')}
              </p>
              <ul className="text-sm text-amber-800 list-disc list-inside mt-1">
                {flags.map((f, idx) => (
                  <li key={idx}>
                    <span className="font-medium">{f.field}</span> — {f.reason}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      )}

      <CaptureManualForm
        key={draft._draftId || draft.title}
        initialValue={initialValue}
        categories={categories}
        onSubmit={onSubmit}
        submitting={submitting}
        submitLabelKey="recipes.draft.useDraft"
      />
    </div>
  )
}
